import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Select,
  Text,
  Spinner,
  SimpleGrid,
  Checkbox,
  IconButton,
  Flex,
  WrapItem,
  Wrap,
  FormLabel,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  AccordionIcon,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const InsertRole = () => {
  const { t } = useTranslation();
  const [roleName, setRoleName] = useState("");
  const [roleCategory, setRoleCategory] = useState("service_provider");
  const [permissions, setPermissions] = useState([
    {
      page_id: "",
      page_access: false,
      read_all_access: false,
      read_one_access: false,
      insert_access: false,
      update_access: false,
      delete_access: false,
      export_access: false,
    },
  ]);
  const pageId = 18;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [pageOptions, setPageOptions] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);

  // Fetch page options from API
  useEffect(() => {
    const fetchPages = async () => {
      setLoadingPages(true);
      try {
        const token = sessionStorage.getItem("accessToken");
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/pages?page_id=${pageId}`,
          "GET",
          null,
          { Authorization: `Bearer ${token}` }
        );
        if (response && response.status === "success" && Array.isArray(response.data)) {
          setPageOptions(response.data);
        } else {
          setError(t("Failed to fetch pages"));
        }
      } catch (err) {
        setError(t("Failed to fetch pages"));
      } finally {
        setLoadingPages(false);
      }
    };
    fetchPages();
  }, [t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const data = {
        role_name: roleName,
        role_category: roleCategory,
        permissions: permissions.map((perm) => ({
          page_id: Number(perm.page_id),
          page_access: perm.page_access,
          read_all_access: perm.read_all_access,
          read_one_access: perm.read_one_access,
          insert_access: perm.insert_access,
          update_access: perm.update_access,
          delete_access: perm.delete_access,
          export_access: perm.export_access,
        })),
      };

      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/roles/store?page_id=${pageId}`,
        "POST",
        JSON.stringify(data),
        { "Content-Type": "application/json" }
      );
      if(response.status !== "success") {
        throw new Error(response.message || "Failed to create role");
      }
      setError("");
      setSuccess(true);
      window.location.reload();
    } catch (err) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermissionChange = (index, field, value) => {
    const updatedPermissions = [...permissions];
    updatedPermissions[index][field] = value;
    setPermissions(updatedPermissions);
  };

  const addPermission = () => {
    setPermissions([
      ...permissions,
      {
        page_id: "",
        page_access: false,
        read_all_access: false,
        read_one_access: false,
        insert_access: false,
        update_access: false,
        delete_access: false,
        export_access: false,
      },
    ]);
  };

  const removePermission = (index) => {
    const updatedPermissions = permissions.filter((_, i) => i !== index);
    setPermissions(updatedPermissions);
  };

  const handleSelectAllForPermission = (index, value) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((perm, i) =>
        i === index
          ? {
              ...perm,
              page_access: value,
              read_all_access: value,
              read_one_access: value,
              insert_access: value,
              update_access: value,
              delete_access: value,
              export_access: value,
            }
          : perm
      )
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Text color="red.500" textAlign="center" fontSize="lg">
              {error}
            </Text>
          )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Role created successfully!")}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="gray.800" mb="2">
                {t("Role Name")}
              </Text>
              <Input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                maxLength={255}
                required
              />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.800" mb="2">
                {t("Role Category")}
              </Text>
              <Select
                variant="filled"
                value={roleCategory}
                onChange={(e) => setRoleCategory(e.target.value)}
                required
              >
                <option value="uclean">{t("Ucleany Admin")}</option>
                <option value="service_provider">{t("Service Provider")}</option>
              </Select>
            </Box>
          </SimpleGrid>

          <Box mt="6">
            <Text fontSize="lg" fontWeight="bold" mb="2">
              {t("Permissions")}
            </Text>

            {/* Accordion for Permissions */}
            <Accordion allowToggle>
              {permissions.map((perm, index) => (
                <AccordionItem key={index} borderWidth="1px" borderRadius="lg" mb={4}>
                  <h2>
                    <AccordionButton _expanded={{ bg: "gray.100" }}>
                      <Box flex="1" textAlign="left">
                        {t("Page")}: {pageOptions.find(p => p.id === parseInt(perm.page_id))?.name || t("Select a Page")}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Box>
                      {/* Page Selection */}
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        {t("Page")}
                      </FormLabel>
                      <Select
                        variant="filled"
                        value={perm.page_id}
                        onChange={(e) =>
                          handlePermissionChange(index, "page_id", e.target.value)
                        }
                        required
                        isDisabled={loadingPages}
                      >
                        <option value="">{loadingPages ? t("Loading...") : t("Select a Page")}</option>
                        {pageOptions.map((page) => (
                          <option key={page.id} value={page.id}>
                            {t(page.name)} ({t(page.category)})
                          </option>
                        ))}
                      </Select>
                    </Box>

                    {/* Select All / Deselect All for Individual Permissions */}
                    <Flex mt={4} gap={2}>
                      <Button
                        colorScheme="teal"
                        size="xs"
                        onClick={() =>
                          handleSelectAllForPermission(index, true)
                        }
                      >
                        {t("Select All")}
                      </Button>
                      <Button
                        colorScheme="gray"
                        size="xs"
                        onClick={() =>
                          handleSelectAllForPermission(index, false)
                        }
                      >
                        {t("Deselect All")}
                      </Button>
                    </Flex>

                    {/* Permissions Checkboxes */}
                    <Box mt={4}>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        {t("Access Rights")}
                      </FormLabel>
                      <Wrap spacing={4}>
                        {[
                          "page_access",
                          "read_all_access",
                          "read_one_access",
                          "insert_access",
                          "update_access",
                          "delete_access",
                          "export_access",
                        ].map((field) => (
                          <WrapItem key={field}>
                            <Checkbox
                              colorScheme="blue"
                              isChecked={perm[field]}
                              onChange={(e) =>
                                handlePermissionChange(index, field, e.target.checked)
                              }
                            >
                              <Text fontSize="sm">
                                {t(field.replace(/_/g, " ").toUpperCase())}
                              </Text>
                            </Checkbox>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    {/* Delete Button */}
                    <Flex justify="flex-end" mt={4}>
                      <IconButton
                        aria-label="Remove permission"
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={() => removePermission(index)}
                      />
                    </Flex>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Add Permission Button */}
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              size="sm"
              mt={4}
              onClick={addPermission}
            >
              {t("Add Permission")}
            </Button>
          </Box>

          {/* Submit Button */}
          <Box>
            <Button
              mt={4}
              color="white"
              colorScheme="cyan"
              fontSize="m"
              borderRadius="16px"
              w={{ base: "100%", md: "148px" }}
              h="46px"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : t("Submit")}
            </Button>
          </Box>
        </form>
      </Card>
    </motion.div>
  );
};

export default InsertRole;